import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { BiData } from '@wix/da-bi/pkg/events';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import ScrollIcon from '@wix/da-ds/pkg/Icons/24x24/Scroll';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { useMeasureElement } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElement';
import noop from '../../utils/noop';
import { useDeviationViewContext } from '../../Context';
import DuperbrowsePaddles from '../../Duperbrowse/DuperbrowsePaddles';
import StageBackground from '../../DeviationBackground/StageBackground';
import { SubscriptionPurchaseFTUEContext } from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import SubscriptionPurchaseFTUE from '../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUE';
import StageContainer from '../_partials/StageContainer';
import AdditionalMediaSlider from '../_partials/AdditionalMediaSlider';
import ZoomModalFromImageSource from '../_partials/ZoomModalFromImageSource';
import StashPaddles from '../_partials/StashPaddles';
import FullImageSourceWithCaption from '../_partials/FullImageSourceWithCaption';
import { BI_CAROUSEL_SECTIONNAME } from '../constants';
import styles from './DesktopArtStage.scss';
/** Multi-image deviation that displays as a slider of thumbnails allowing the user
 *  to choose what image is displayed in a larger single image above.
 *
 *  Also currently the only multi-image stage that correctly shows mature, paywall
 *  blocking. */ export const DesktopMultiArtCarouselStage = ({ deviation, additionalMedia, zoomedImageSource, isZoomed, disabled, shouldBlur, currentlyViewedImageSource, setCurrentlyViewedImageSource = noop, className, onEnterZoomMode, onExitZoomMode, setDisplayMode = noop })=>{
    const { t } = useTranslation();
    const { isFullscreen } = useDeviationViewContext();
    const { ref: contentRef, dimensions: contentSize = {} } = useMeasureElementCookie(isFullscreen ? undefined : MeasuredCookieType.DEVIATION_MULTI_IMAGE_CAROUSEL, {
        cookieDim: 'both'
    });
    const { ref: containerRef, dimensions: containerSize } = useMeasureElement();
    const isSubscriptionPurchaseFTUEBeingShown = !!useContext(SubscriptionPurchaseFTUEContext);
    var _zoomedImageSource_isPublished;
    return /*#__PURE__*/ React.createElement(StageContainer, {
        className: classnames(className, styles['root'], styles['carousel-mode'], isFullscreen && styles['fullscreen']),
        withoutInnerWrapper: true,
        ref: containerRef
    }, /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: BI_CAROUSEL_SECTIONNAME
    }, /*#__PURE__*/ React.createElement("div", {
        className: classnames(styles['content']),
        ref: contentRef
    }, /*#__PURE__*/ React.createElement(StageBackground, {
        deviation: deviation,
        width: containerSize === null || containerSize === void 0 ? void 0 : containerSize.width,
        height: containerSize === null || containerSize === void 0 ? void 0 : containerSize.height
    }), /*#__PURE__*/ React.createElement(FullImageSourceWithCaption, _object_spread_props(_object_spread({
        deviationOrAttachment: currentlyViewedImageSource ? currentlyViewedImageSource : deviation,
        maintainHeightForCaptions: true
    }, contentSize), {
        withoutZoomModal: true,
        isZoomed: isZoomed,
        onEnterZoomMode: onEnterZoomMode,
        onExitZoomMode: onExitZoomMode
    })), deviation.isPublished && /*#__PURE__*/ React.createElement(DuperbrowsePaddles, {
        className: styles['duperbrowse'],
        backToStreamButtonClassName: styles['back-to-stream-button']
    }), !deviation.isPublished && /*#__PURE__*/ React.createElement(StashPaddles, null)), /*#__PURE__*/ React.createElement(Section, {
        className: styles['carousel-controls']
    }, /*#__PURE__*/ React.createElement(VisuallyHidden, null, /*#__PURE__*/ React.createElement(Heading, null, t('deviationview.artstage.multi.carouselControls.a11yHeading'))), /*#__PURE__*/ React.createElement(AdditionalMediaSlider, {
        className: styles['additional-media-slider'],
        deviation: deviation,
        additionalMedia: additionalMedia,
        disabled: disabled,
        shouldBlur: shouldBlur,
        selected: currentlyViewedImageSource,
        onSelect: (attachment)=>setCurrentlyViewedImageSource(attachment)
    }), /*#__PURE__*/ React.createElement(IconButton, {
        className: styles['switch-to-scroll-button'],
        icon: ScrollIcon,
        size: "large",
        tooltipText: t('deviationview.artstage.changeToScrollMode.tooltip'),
        tooltipOffset: [
            0,
            14
        ],
        "aria-label": t('deviationview.artstage.changeToScrollMode.altLabel'),
        disabled: disabled,
        onClick: ()=>setDisplayMode('scroll'),
        biData: BiData({
            evid: 309,
            deviationid: deviation.deviationId,
            view_mode: 'scroll'
        })
    })), zoomedImageSource && /*#__PURE__*/ React.createElement(ZoomModalFromImageSource, _object_spread_props(_object_spread({
        source: zoomedImageSource,
        allowFullviewSrcsetForPngs: (_zoomedImageSource_isPublished = zoomedImageSource === null || zoomedImageSource === void 0 ? void 0 : zoomedImageSource.isPublished) !== null && _zoomedImageSource_isPublished !== void 0 ? _zoomedImageSource_isPublished : false
    }, contentSize), {
        isZoomed: isZoomed,
        onExitZoomMode: onExitZoomMode
    })), isSubscriptionPurchaseFTUEBeingShown && /*#__PURE__*/ React.createElement(SubscriptionPurchaseFTUE, {
        className: styles['subscription-purchase-ftue']
    })));
};
DesktopMultiArtCarouselStage.displayName = 'DesktopMultiArtCarouselStage';
export default DesktopMultiArtCarouselStage;

//# sourceMappingURL=DesktopMultiArtCarouselStage.js.map